import { useEffect, useRef, useState } from "react";
import type { Sheet } from "../shared/schema";
import type { WorkspaceModel } from "./useWorkspace";
import { notify } from "./api";
import { Download } from "lucide-react";
import "./cad-drawing.css";

export function CadDrawingControls({
  model,
  sheet,
}: {
  model: WorkspaceModel;
  sheet: Sheet;
}) {
  const views = Array.isArray(sheet.meta.cadViews)
    ? sheet.meta.cadViews.filter((v): v is string => typeof v === "string")
    : [];
  const warnings = Array.isArray(sheet.meta.cadWarnings)
    ? sheet.meta.cadWarnings.filter((v): v is string => typeof v === "string")
    : [];
  // Offer a view other than the one this sheet already shows.
  const [view, setView] = useState(
      views.find((name) => name !== sheet.meta.cadView) ?? views[0] ?? "Model",
    ),
    [busy, setBusy] = useState(false);
  const job = useRef<AbortController | null>(null);
  useEffect(() => () => job.current?.abort(), []);
  const source = String(sheet.meta.cadSource ?? "");
  // The board validator already restricts this to a project file; keep the
  // client from fetching or linking anything else if an older record slips by.
  if (
    sheet.meta.cadFormat !== "DXF" ||
    !/^\/api\/rooms\/[\w-]+\/files\/[\w-]+$/.test(source)
  )
    return null;
  return (
    <div
      className="inspector-section cad-drawing-controls"
      aria-label="AutoCAD drawing"
    >
      <div className="property-row">
        <span>DXF view</span>
        <strong>{String(sheet.meta.cadView)}</strong>
      </div>
      <div className="property-row">
        <span>File units</span>
        <strong>{String(sheet.meta.cadUnits)}</strong>
      </div>
      {warnings.length > 0 && (
        <details>
          <summary>Drawing import notes</summary>
          {warnings.map((warning) => (
            <p key={warning} className="hint">
              {warning}
            </p>
          ))}
        </details>
      )}
      {views.length > 1 && (
        <>
          <label>
            Another drawing view
            <select
              aria-label="DXF drawing view"
              value={view}
              disabled={busy || !model.canEdit}
              onChange={(e) => setView(e.target.value)}
            >
              {views.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <button
            className="calibrate-button"
            disabled={busy || !model.canEdit}
            onClick={async () => {
              if (job.current) return;
              const controller = new AbortController();
              job.current = controller;
              setBusy(true);
              try {
                const res = await fetch(source, {
                  signal: controller.signal,
                });
                if (!res.ok) throw new Error("Cannot read the original DXF.");
                const file = new File(
                  [await res.blob()],
                  String(sheet.meta.cadName),
                  { type: "application/dxf" },
                );
                const bounds = model.editor.getShapePageBounds(sheet)!;
                const { importDxfDrawing } = await import("./dxf-imports");
                await importDxfDrawing(
                  model.editor,
                  model.room.id,
                  file,
                  controller.signal,
                  model.room.platform === "cloudflare",
                  {
                    source,
                    view,
                    placement: {
                      parentId: model.editor.getCurrentPageId(),
                      center: { x: bounds.maxX + 660, y: bounds.center.y },
                    },
                  },
                );
              } catch (e) {
                if (!controller.signal.aborted) notify((e as Error).message);
              } finally {
                job.current = null;
                setBusy(false);
              }
            }}
          >
            {busy ? "Adding drawing…" : "Add view as a new drawing"}
          </button>
        </>
      )}
      <a
        className="cad-original"
        href={source}
        download={String(sheet.meta.cadName)}
      >
        <Download size={15} /> Download original DXF
      </a>
    </div>
  );
}
